import ffs_parser from "./ffs/ffs.pegjs";
// ffs/wizard module
import ffs_free from "./ffs/free";

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
let freeFormQuery;

type Query = {
  logical: "or" | "xor" | "minus" | "and";
  queries: Query[];
};

/** a single search condition, as produced by the ffs parser */
type Condition = {
  query: string;
  key?: string;
  /** a plain value, or `{regex, modifier}` for the matching queries */
  val?;
  meta?: string;
  type?: string;
  free?: string;
};

/* this converts a random boolean expression into a normalized form:
 * A∧B∧… ∨ C∧D∧… ∨ …
 * for example: A∧(B∨C) ⇔ (A∧B)∨(A∧C)
 */
function normalize(query: Query): Query {
  function normalize_recursive(rem_query: Query): Query[] {
    if (!rem_query.logical) {
      return [
        {
          logical: "and",
          queries: [rem_query]
        }
      ];
    } else if (rem_query.logical === "and") {
      const c1 = normalize_recursive(rem_query.queries[0]);
      const c2 = normalize_recursive(rem_query.queries[1]);
      // return cross product of c1 and c2
      return c1.flatMap((c1i) =>
        c2.map((c2j) => ({
          logical: "and",
          queries: c1i.queries.concat(c2j.queries)
        }))
      );
    } else if (rem_query.logical === "or") {
      const c1 = normalize_recursive(rem_query.queries[0]);
      const c2 = normalize_recursive(rem_query.queries[1]);
      return [].concat(c1, c2);
    } else {
      alert(`unsupported boolean operator: ${rem_query.logical}`);
    }
  }
  return {
    logical: "or",
    queries: normalize_recursive(query)
  };
}

function escRegexp(str: string): string {
  return str.replace(/([()[{*+.$^\\|?])/g, "\\$1");
}

/* interprets the value of a `newer:`/`older:`/`date:` condition:
 * abbreviated dates are expanded to the full `yyyy-mm-ddThh:mm:ssZ` format
 * expected by the overpass API, e.g. `2025-12-01` → `2025-12-01T00:00:00Z`,
 * while relative dates are turned into a `{{date:…}}` shortcut.
 * a bare four digit number in a plausible year range is treated as a year,
 * e.g. `2025` → `2025-01-01T00:00:00Z`, and not as a number of days.
 * any other value is returned unchanged.
 */
function dateValue(val: string): string {
  const [
    ,
    year,
    month = "01",
    day = "01",
    hour = "00",
    minute = "00",
    second = "00"
  ] =
    val.match(
      /^(\d{4})(?:-(\d{2})(?:-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?)?)?Z?$/
    ) ?? [];
  // a number of days is more likely intended outside of this range
  if (year && +year >= 1900 && +year <= 2099)
    return `${year}-${month}-${day}T${hour}:${minute}:${second}Z`;
  if (
    val.match(
      /^-?\d+ ?(seconds?|minutes?|hours?|days?|weeks?|months?|years?)?$/
    )
  )
    return `{{date:${val}}}`;
  return val;
}

export async function ffs_construct_query(
  search: string,
  /** the comment to head the query with, `true` for the default one, or
   * `false` to generate no comments at all */
  comment: string | boolean | undefined
): Promise<string> {
  function quote_comment_str(s: string): string {
    // quote strings that are to be used within c-style comments
    // replace any comment-ending sequences in these strings that would break the resulting query
    return s.replace(/\*\//g, "[…]").replace(/\n/g, "\\n");
  }

  let ffs;
  try {
    ffs = ffs_parser.parse(search);
  } catch (e) {
    console.warn("ffs parse error", e);
    throw new Error("ffs parse error");
  }

  ffs.query = normalize(ffs.query);

  // `date:` is a global setting (attic data), not a per-object condition:
  // take it out of the query clauses and put it into the settings line
  let date_setting = "";
  for (const and_query of ffs.query.queries) {
    and_query.queries = and_query.queries.filter((cond_query: Condition) => {
      if (cond_query.query !== "meta" || cond_query.meta !== "date")
        return true;
      date_setting = `[date:"${dateValue(cond_query.val)}"]`;
      return false;
    });
  }

  const query_parts = [];
  let bounds_part;

  function add_comment(string: string): void {
    if (comment !== false) {
      query_parts.push(string);
    }
  }

  add_comment("/*");
  if (typeof comment === "string") {
    add_comment(comment);
  } else {
    add_comment("This has been generated by the overpass-turbo wizard.");
    add_comment("The original search was:");
    add_comment(`“${quote_comment_str(search)}”`);
  }
  add_comment("*/");
  query_parts.push(`[out:json][timeout:25]${date_setting};`);

  switch (ffs.bounds) {
    case "area":
      add_comment(`// fetch area “${ffs.area}” to search in`);
      query_parts.push(`{{geocodeArea:${ffs.area}}}->.searchArea;`);
      bounds_part = "(area.searchArea)";
      break;
    case "around":
      add_comment("// adjust the search radius (in meters) here");
      query_parts.push("{{radius=1000}}");
      bounds_part = `(around:{{radius}},{{geocodeCoords:${ffs.area}}})`;
      break;
    case "bbox":
      bounds_part = "({{bbox}})";
      break;
    case "global":
      bounds_part = undefined;
      break;
    default:
      alert(`unknown bounds condition: ${ffs.bounds}`);
      throw new Error(`unknown bounds condition: ${ffs.bounds}`);
  }

  function get_query_clause(condition: Condition): string | false {
    function esc(str: string): string | undefined {
      if (typeof str !== "string") return;
      // see http://wiki.openstreetmap.org/wiki/Overpass_API/Overpass_QL#Escaping
      return str
        .replace(/\\/g, "\\\\")
        .replace(/"/g, '\\"') // need to escape those
        .replace(/\t/g, "\\t")
        .replace(/\n/g, "\\n"); // also escape newlines an tabs for better readability of the query
    }
    let key = esc(condition.key);
    const val = esc(condition.val);
    // convert substring searches into matching regexp ones
    if (condition.query === "substr") {
      condition.query = "like";
      condition.val = {regex: escRegexp(condition.val)};
    }
    // special case for empty values
    // see https://github.com/drolbr/Overpass-API/issues/53
    if (val === "") {
      if (condition.query === "eq") {
        condition.query = "like";
        condition.val = {regex: "^$"};
      } else if (condition.query === "neq") {
        condition.query = "notlike";
        condition.val = {regex: "^$"};
      }
    }
    // special case for empty keys
    // see https://github.com/drolbr/Overpass-API/issues/53#issuecomment-26325122
    if (key === "") {
      if (condition.query === "key") {
        condition.query = "likelike";
        key = "^$";
        condition.val = {regex: ".*"};
      } else if (condition.query === "eq") {
        condition.query = "likelike";
        key = "^$";
        condition.val = {regex: `^${escRegexp(condition.val)}$`};
      } else if (condition.query === "like") {
        condition.query = "likelike";
        key = "^$";
      }
    }
    // construct the query clause
    switch (condition.query) {
      case "key":
        return `["${key}"]`;
      case "nokey":
        return `["${key}"!~".*"]`;
      case "eq":
        return `["${key}"="${val}"]`;
      case "neq":
        return `["${key}"!="${val}"]`;
      case "like":
        return `["${key}"~"${esc(condition.val.regex)}"${
          condition.val.modifier === "i" ? ",i" : ""
        }]`;
      case "likelike":
        return `[~"${key}"~"${esc(condition.val.regex)}"${
          condition.val.modifier === "i" ? ",i" : ""
        }]`;
      case "notlike":
        return `["${key}"!~"${esc(condition.val.regex)}"${
          condition.val.modifier === "i" ? ",i" : ""
        }]`;
      case "meta":
        switch (condition.meta) {
          case "id":
            return `(${val})`;
          case "newer":
            return `(newer:"${dateValue(val)}")`;
          case "older":
            return `(if: timestamp() <= "${dateValue(val)}")`;
          case "user":
            return `(user:"${val}")`;
          case "uid":
            return `(uid:${val})`;
          default:
            console.log(`unknown query type: meta/${condition.meta}`);
            return false;
        }
      case "free form":
      // own module, special cased below
      // falls through
      default:
        console.log(`unknown query type: ${condition.query}`);
        return false;
    }
  }

  let freeForm = false;
  for (const and_query of ffs.query.queries) {
    for (const cond_query of and_query.queries) {
      if (cond_query.query === "free form") {
        freeForm = true;
        break;
      }
    }
  }

  // if we have a "free form" query part, need to load it before first use:
  const freeFormQuery = freeForm ? await ffs_free() : null;

  add_comment("// gather results");
  query_parts.push("(");
  for (const and_query of ffs.query.queries) {
    let types = ["node", "way", "relation"];
    let clauses = [];
    for (const cond_query of and_query.queries) {
      // todo: looks like some code duplication here could be reduced by refactoring
      if (cond_query.query === "free form") {
        const ffs_clause = freeFormQuery.get_query_clause(cond_query);
        if (ffs_clause === false) throw new Error("unknown ffs string");
        // restrict possible data types
        types = types.filter((t) => ffs_clause.types.indexOf(t) != -1);
        // add clauses
        clauses = clauses.concat(
          ffs_clause.conditions.map((condition) => get_query_clause(condition))
        );
      } else if (cond_query.query === "type") {
        // restrict possible data types
        types = types.indexOf(cond_query.type) != -1 ? [cond_query.type] : [];
      } else {
        // add another query clause
        const clause = get_query_clause(cond_query);
        if (clause === false) throw new Error("unknown query clause");
        clauses.push(clause);
      }
    }

    // construct query
    if (types.length === 3) {
      types = ["nwr"];
    }
    for (const t of types) {
      let buffer = `  ${t}`;
      for (const c of clauses) buffer += c;
      if (bounds_part) buffer += bounds_part;
      buffer += ";";
      query_parts.push(buffer);
    }
  }

  if (query_parts.indexOf("(") === query_parts.length - 2) {
    const idx = query_parts.indexOf("(");
    query_parts.splice(idx, 1);
    query_parts[idx] = query_parts[idx].substr(2);
  } else {
    query_parts.push(");");
  }

  add_comment("// print results");
  query_parts.push("out geom;");

  return query_parts.join("\n");
}

/** raised when a query could not be parsed, but a correction was found */
export class FfsRepairError extends Error {
  readonly repaired: string[];

  constructor(repaired: string[]) {
    super("repairable query");
    this.name = "FfsRepairError";
    this.repaired = repaired;
  }
}

/**
 * this is a "did you mean …" mechanism against typos in preset names
 */
export async function ffs_repair_search(
  search: string
): Promise<string[] | false> {
  let ffs;
  try {
    ffs = ffs_parser.parse(search);
  } catch {
    return false;
  }

  function quotes(s: string): string {
    if (s.match(/^[a-zA-Z0-9_]+$/) === null)
      return `"${s.replace(/"/g, '\\"')}"`;
    return s;
  }

  let search_parts = [];
  let repaired = false;

  const freeFormQuery = await ffs_free();
  ffs.query = normalize(ffs.query);
  ffs.query.queries.forEach((q) => {
    q.queries.forEach(validateQuery);
  });
  function validateQuery(cond_query: Condition): void {
    if (cond_query.query === "free form") {
      const ffs_clause = freeFormQuery.get_query_clause(cond_query);
      if (ffs_clause === false) {
        // try to find suggestions for occasional typos
        const fuzzy = freeFormQuery.fuzzy_search(cond_query);
        let free_regex = null;
        try {
          free_regex = new RegExp(`['"]?${escRegexp(cond_query.free)}['"]?`);
        } catch {}
        if (fuzzy && search.match(free_regex)) {
          search_parts = search_parts.concat(search.split(free_regex));
          search = search_parts.pop();
          const replacement = quotes(fuzzy);
          search_parts.push(replacement);
          repaired = true;
        }
      }
    }
  }
  search_parts.push(search);

  return repaired ? search_parts : false;
}

export function ffs_invalidateCache() {
  freeFormQuery = undefined;
}
